import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2, CheckCheck } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import type { AppNotification } from '@/types';
import {
  getNotifications,
  getUnreadCount,
  markRead,
  markAllRead,
} from '@/services/notifications.service';

// Interval de reîmprospătare a contorului de necitite (~60s)
const POLL_INTERVAL_MS = 60_000;

// Formatare timp relativ în română (ex. „acum 5 minute")
function formatRelative(dateStr: string): string {
  try {
    return formatDistanceToNow(parseISO(dateStr), {
      addSuffix: true,
      locale: ro,
    });
  } catch {
    return '';
  }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reîmprospătare contor necitite (poll periodic + la deschidere)
  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Nu s-a putut obține numărul de notificări necitite:', err);
    }
  }, []);

  // Încărcare listă notificări (la deschiderea dropdown-ului)
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await getNotifications({ limit: 20 });
      setNotifications(list);
    } catch (err) {
      console.error('Nu s-au putut încărca notificările:', err);
      toast.error('Nu s-au putut încărca notificările');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Poll periodic pentru badge, curățat la unmount
  useEffect(() => {
    refreshUnreadCount();
    const intervalId = window.setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [refreshUnreadCount]);

  // Închide dropdown-ul la click în afară
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      loadNotifications();
      refreshUnreadCount();
    }
  };

  // Click pe o notificare: marchează citit + navighează la link
  const handleNotificationClick = async (notif: AppNotification) => {
    setIsOpen(false);
    if (!notif.is_read) {
      // Actualizare optimistă a stării locale
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await markRead(notif.id);
      } catch (err) {
        console.error('Nu s-a putut marca notificarea ca citită:', err);
      }
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  // Marchează toate ca citite
  const handleMarkAllRead = async () => {
    if (isMarkingAll || unreadCount === 0) return;
    setIsMarkingAll(true);
    try {
      await markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Toate notificările au fost marcate ca citite');
    } catch (err) {
      console.error('Nu s-au putut marca notificările:', err);
      toast.error('Nu s-au putut marca notificările');
    } finally {
      setIsMarkingAll(false);
    }
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={containerRef}>
      {/* Buton clopoțel cu badge de necitite */}
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notificări"
        title="Notificări"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none">
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Dropdown cu notificări */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 flex flex-col max-h-[70vh]">
          {/* Antet */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">Notificări</p>
            <button
              onClick={handleMarkAllRead}
              disabled={isMarkingAll || unreadCount === 0}
              className={clsx(
                'flex items-center gap-1.5 text-xs font-medium transition-colors',
                unreadCount === 0 || isMarkingAll
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-primary-600 hover:text-primary-700'
              )}
            >
              {isMarkingAll ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCheck className="w-3.5 h-3.5" />
              )}
              Marchează toate citite
            </button>
          </div>

          {/* Listă */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Nu ai notificări.
              </div>
            ) : (
              <ul>
                {notifications.map((notif) => (
                  <li key={notif.id}>
                    <button
                      onClick={() => handleNotificationClick(notif)}
                      className={clsx(
                        'w-full text-left px-4 py-3 flex gap-3 transition-colors border-b border-gray-50 last:border-b-0',
                        notif.is_read ? 'hover:bg-gray-50' : 'bg-primary-50/60 hover:bg-primary-50'
                      )}
                    >
                      {/* Indicator necitit */}
                      <span
                        className={clsx(
                          'mt-1.5 w-2 h-2 rounded-full flex-shrink-0',
                          notif.is_read ? 'bg-transparent' : 'bg-primary-600'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={clsx(
                            'text-sm break-words',
                            notif.is_read
                              ? 'text-gray-600 font-normal'
                              : 'text-gray-900 font-medium'
                          )}
                        >
                          {notif.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatRelative(notif.created_at)}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
